import { useMemo, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { Upload, FileSpreadsheet } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NONE = "__none__";
const DERIVE = "__derive__";
const NOHEADER = "__noheader__";

const HEADER_KEYWORDS = ["date", "amount", "description", "vendor", "payee", "memo", "debit", "credit",
  "gst", "tax", "hst", "category", "total", "balance", "posting", "transaction", "details", "price",
  "qty", "currency", "cad", "supplier", "withdrawal", "deposit", "reference", "period", "business"];

const parseAmount = (v) => {
  if (v == null) return NaN;
  let s = String(v).trim();
  if (!s) return NaN;
  const neg = /^\(.*\)$/.test(s);
  s = s.replace(/[()]/g, "");
  const n = parseFloat(s.replace(/[^0-9.\-]/g, ""));
  if (isNaN(n)) return NaN;
  return neg ? -Math.abs(n) : n;
};

const parseDate = (v) => {
  if (!v) return "";
  if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/);
  if (m) {
    let [, a, b, y] = m;
    if (y.length === 2) y = "20" + y;
    return `${y}-${String(a).padStart(2, "0")}-${String(b).padStart(2, "0")}`;
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return s;
};

// Score a row on how header-like it is (matches known field keywords).
const headerScore = (row) => {
  const cells = row.map((c) => String(c).toLowerCase().trim()).filter(Boolean);
  if (cells.length < 2) return 0;
  return cells.filter((c) => HEADER_KEYWORDS.some((k) => c.includes(k))).length;
};

// Detect header row index (-1 = no header) by picking the highest-scoring row.
const detectHeader = (rows) => {
  let best = -1, bestScore = 1; // require at least 2 keyword hits
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const s = headerScore(rows[i]);
    if (s > bestScore) { bestScore = s; best = i; }
  }
  return best; // -1 if nothing scored >=2
};

const guessSheet = (names) => {
  const lower = names.map((n) => n.toLowerCase());
  const pref = ["statement", "transaction", "expense", "detail", "activity"];
  for (const p of pref) {
    const idx = lower.findIndex((n) => n.includes(p));
    if (idx >= 0) return names[idx];
  }
  return names[0];
};

export const ImportExpensesDialog = ({ open, onOpenChange, categories, jobs, onImported }) => {
  const [wb, setWb] = useState(null);          // XLSX workbook (Excel only)
  const [sheetNames, setSheetNames] = useState([]);
  const [sheetName, setSheetName] = useState("");
  const [rawRows, setRawRows] = useState([]);  // array of arrays for the active sheet/file
  const [headerRow, setHeaderRow] = useState(0);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const [map, setMap] = useState({ date: NONE, description: NONE, amount: NONE, vendor: DERIVE, gst_col: NONE });
  const [sign, setSign] = useState("abs");
  const [gstMode, setGstMode] = useState("auto");
  const [category, setCategory] = useState("General");
  const [job, setJob] = useState(NONE);

  const reset = () => {
    setWb(null); setSheetNames([]); setSheetName(""); setRawRows([]); setHeaderRow(0); setFileName("");
    setMap({ date: NONE, description: NONE, amount: NONE, vendor: DERIVE, gst_col: NONE });
    setSign("abs"); setGstMode("auto"); setCategory("General"); setJob(NONE);
  };

  const handleClose = (o) => { if (!o) reset(); onOpenChange(o); };

  const maxCols = useMemo(
    () => rawRows.slice(0, 40).reduce((m, r) => Math.max(m, r.length), 0),
    [rawRows]
  );

  const columns = useMemo(() => {
    const hdr = headerRow >= 0 ? rawRows[headerRow] || [] : [];
    return Array.from({ length: maxCols }, (_, i) => ({
      idx: String(i),
      label: (headerRow >= 0 && hdr[i] && String(hdr[i]).trim()) || `Column ${i + 1}`,
    }));
  }, [rawRows, headerRow, maxCols]);

  const dataRows = useMemo(
    () => (headerRow >= 0 ? rawRows.slice(headerRow + 1) : rawRows),
    [rawRows, headerRow]
  );

  const autoMap = (cols, hasHeader) => {
    if (!hasHeader) return { date: NONE, description: NONE, amount: NONE, vendor: DERIVE, gst_col: NONE };
    const find = (cands, exclude = []) => {
      const c = cols.find((col) => {
        const l = col.label.toLowerCase();
        return cands.some((k) => l.includes(k)) && !exclude.some((k) => l.includes(k));
      });
      return c ? c.idx : null;
    };
    return {
      date: find(["transaction date", "date"]) || NONE,
      description: find(["description", "memo", "details", "narration"]) || NONE,
      amount: find(["amount (cad)", "amount", "debit", "value", "withdrawal", "total"], ["fx"]) || NONE,
      vendor: find(["vendor", "payee", "merchant", "supplier"]) || DERIVE,
      gst_col: find(["gst", "hst", "tax"]) || NONE,
    };
  };

  const applyRows = (rows) => {
    const clean = rows.filter((r) => Array.isArray(r) && r.some((c) => String(c).trim() !== ""));
    setRawRows(clean);
    const hr = detectHeader(clean);
    setHeaderRow(hr);
    const cols = Array.from({ length: clean.reduce((m, r) => Math.max(m, r.length), 0) }, (_, i) => ({
      idx: String(i),
      label: (hr >= 0 && clean[hr] && clean[hr][i] && String(clean[hr][i]).trim()) || `Column ${i + 1}`,
    }));
    setMap(autoMap(cols, hr >= 0));
  };

  const loadSheet = (workbook, name) => {
    const ws = workbook.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "", blankrows: false });
    setSheetName(name);
    applyRows(rows);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    const reader = new FileReader();
    reader.onerror = () => toast.error("Could not read that file");
    if (isExcel) {
      reader.onload = () => {
        try {
          const workbook = XLSX.read(new Uint8Array(reader.result), { type: "array", cellDates: true });
          const names = workbook.SheetNames || [];
          if (!names.length) { toast.error("No sheets found in that workbook"); return; }
          setWb(workbook);
          setSheetNames(names);
          loadSheet(workbook, guessSheet(names));
        } catch (err) {
          toast.error("Could not read that Excel file");
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => {
        let text = String(reader.result || "");
        if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
        const res = Papa.parse(text, { header: false, skipEmptyLines: "greedy" });
        if (!res.data || !res.data.length) { toast.error("That file looks empty"); return; }
        setWb(null); setSheetNames([]); setSheetName("");
        applyRows(res.data);
      };
      reader.readAsText(file);
    }
  };

  const changeSheet = (name) => { if (wb) loadSheet(wb, name); };

  const changeHeader = (val) => {
    const hr = val === NOHEADER ? -1 : parseInt(val, 10);
    setHeaderRow(hr);
    const hdr = hr >= 0 ? rawRows[hr] || [] : [];
    const cols = Array.from({ length: maxCols }, (_, i) => ({
      idx: String(i),
      label: (hr >= 0 && hdr[i] && String(hdr[i]).trim()) || `Column ${i + 1}`,
    }));
    setMap(autoMap(cols, hr >= 0));
  };

  const cell = (row, sel) => (sel === NONE || sel === DERIVE ? "" : row[parseInt(sel, 10)]);

  const buildRows = () => {
    if (map.amount === NONE || map.date === NONE) return [];
    const out = [];
    for (const r of dataRows) {
      const rawAmt = parseAmount(cell(r, map.amount));
      if (isNaN(rawAmt)) continue;
      if (sign === "neg" && rawAmt >= 0) continue;
      if (sign === "pos" && rawAmt <= 0) continue;
      const amount = Math.abs(rawAmt);
      if (amount === 0) continue;
      const description = map.description !== NONE ? String(cell(r, map.description) || "").trim() : "";
      const vendor = map.vendor === DERIVE || map.vendor === NONE
        ? (description || "Imported expense")
        : String(cell(r, map.vendor) || "").trim() || (description || "Imported expense");
      let gst_amount = null;
      if (gstMode === "column" && map.gst_col !== NONE) gst_amount = parseAmount(cell(r, map.gst_col)) || 0;
      else if (gstMode === "none") gst_amount = 0;
      out.push({
        vendor, category, description, amount, gst_amount,
        date: parseDate(cell(r, map.date)),
        is_subscription: false,
        job_id: job === NONE ? null : job,
        job_charge: false,
      });
    }
    return out;
  };

  const preview = buildRows();

  const doImport = async () => {
    const items = buildRows();
    if (!items.length) return toast.error("Nothing to import — check your Date and Amount mappings");
    setSaving(true);
    try {
      const res = await api.post("/expenses/bulk", items, { params: { file_name: fileName } });
      toast.success(`Imported ${res.data.created} expense${res.data.created === 1 ? "" : "s"}`);
      reset();
      onOpenChange(false);
      onImported?.();
    } catch (e) {
      toast.error("Import failed");
    } finally {
      setSaving(false);
    }
  };

  const colOptions = (includeDerive) => (
    <SelectContent className="bg-white max-h-72">
      {includeDerive && <SelectItem value={DERIVE}>Derive from Description</SelectItem>}
      <SelectItem value={NONE}>— none —</SelectItem>
      {columns.map((c) => <SelectItem key={c.idx} value={c.idx}>{c.label}</SelectItem>)}
    </SelectContent>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">Import Expenses</DialogTitle>
        </DialogHeader>

        {rawRows.length === 0 ? (
          <label
            data-testid="import-csv-dropzone"
            className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 py-12 cursor-pointer hover:border-slate-300 hover:bg-slate-50"
          >
            <FileSpreadsheet className="h-8 w-8 text-slate-400" />
            <span className="text-sm text-slate-600">Click to choose a CSV or Excel file</span>
            <span className="text-xs text-slate-400">Bank export or spreadsheet — .csv, .xlsx (multi-sheet ok)</span>
            <input type="file" accept=".csv,.xlsx,.xls,text/csv" className="hidden" data-testid="import-csv-file" onChange={onFile} />
          </label>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{fileName} · {dataRows.length} data rows</span>
              <button className="text-blue-600 hover:underline" data-testid="import-csv-change" onClick={reset}>Choose a different file</button>
            </div>

            {sheetNames.length > 1 && (
              <div>
                <Label>Sheet</Label>
                <Select value={sheetName} onValueChange={changeSheet}>
                  <SelectTrigger data-testid="map-sheet" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    {sheetNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">This workbook has multiple tabs — pick the one with your transactions.</p>
              </div>
            )}

            <div>
              <Label>Which row has the column headings?</Label>
              <Select value={headerRow < 0 ? NOHEADER : String(headerRow)} onValueChange={changeHeader}>
                <SelectTrigger data-testid="map-header-row" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  <SelectItem value={NOHEADER}>No header row (use Column 1, 2, 3…)</SelectItem>
                  {rawRows.slice(0, 10).map((r, i) => (
                    <SelectItem key={i} value={String(i)}>
                      Row {i + 1}: {r.filter((c) => String(c).trim() !== "").join(" | ").slice(0, 60) || "(blank)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400 mt-1">Pick the row with labels like Date, Amount, Vendor. Rows above it are ignored.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date column *</Label>
                <Select value={map.date} onValueChange={(v) => setMap({ ...map, date: v })}>
                  <SelectTrigger data-testid="map-date" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  {colOptions(false)}
                </Select>
              </div>
              <div>
                <Label>Amount column *</Label>
                <Select value={map.amount} onValueChange={(v) => setMap({ ...map, amount: v })}>
                  <SelectTrigger data-testid="map-amount" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  {colOptions(false)}
                </Select>
              </div>
              <div>
                <Label>Description column</Label>
                <Select value={map.description} onValueChange={(v) => setMap({ ...map, description: v })}>
                  <SelectTrigger data-testid="map-description" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  {colOptions(false)}
                </Select>
              </div>
              <div>
                <Label>Vendor</Label>
                <Select value={map.vendor} onValueChange={(v) => setMap({ ...map, vendor: v })}>
                  <SelectTrigger data-testid="map-vendor" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  {colOptions(true)}
                </Select>
              </div>
              <div>
                <Label>Amount sign</Label>
                <Select value={sign} onValueChange={setSign}>
                  <SelectTrigger data-testid="map-sign" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="abs">Import all (use absolute value)</SelectItem>
                    <SelectItem value="neg">Only negatives are expenses (skip deposits)</SelectItem>
                    <SelectItem value="pos">Only positives are expenses (skip credits)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST handling</Label>
                <Select value={gstMode} onValueChange={setGstMode}>
                  <SelectTrigger data-testid="map-gst-mode" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="auto">Auto-calc 5% on each row</SelectItem>
                    <SelectItem value="column">Use a GST column</SelectItem>
                    <SelectItem value="none">No GST</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {gstMode === "column" && (
                <div>
                  <Label>GST column</Label>
                  <Select value={map.gst_col} onValueChange={(v) => setMap({ ...map, gst_col: v })}>
                    <SelectTrigger data-testid="map-gst-col" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                    {colOptions(false)}
                  </Select>
                </div>
              )}
              <div>
                <Label>Category (all rows)</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="map-category" className="mt-1 rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assign to Job (optional)</Label>
                <Select value={job} onValueChange={setJob}>
                  <SelectTrigger data-testid="map-job" className="mt-1 rounded-md"><SelectValue placeholder="No job" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72">
                    <SelectItem value={NONE}>No job</SelectItem>
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}{j.code ? ` (${j.code})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="text-sm font-medium text-slate-900 mb-1">Preview — {preview.length} expense{preview.length === 1 ? "" : "s"} will import</div>
              <div className="rounded-md border border-slate-200 overflow-hidden">
                <table className="w-full text-sm" data-testid="import-preview-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                      <th className="px-3 py-2 font-medium">Date</th>
                      <th className="px-3 py-2 font-medium">Vendor</th>
                      <th className="px-3 py-2 font-medium">Description</th>
                      <th className="px-3 py-2 font-medium text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 6).map((r, i) => (
                      <tr key={i} className="border-b border-slate-50">
                        <td className="px-3 py-1.5 text-slate-600">{r.date}</td>
                        <td className="px-3 py-1.5 text-slate-900">{r.vendor}</td>
                        <td className="px-3 py-1.5 text-slate-500 truncate max-w-[220px]">{r.description}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{money(r.amount)}</td>
                      </tr>
                    ))}
                    {preview.length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-6 text-center text-slate-400">Map Date and Amount to see a preview.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} className="rounded-md">Cancel</Button>
          <Button
            data-testid="import-csv-confirm"
            onClick={doImport}
            disabled={saving || preview.length === 0}
            className="rounded-md bg-slate-900 hover:bg-slate-800"
          >
            <Upload className="h-4 w-4 mr-1" /> {saving ? "Importing…" : `Import ${preview.length || ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
